import { FunctionComponent, useEffect, useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";

import useLocalStorageState from "use-local-storage-state";

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { Table, TableColumn } from "@/components/Table/Table";
import { TItem } from "@/components/SortableList/SortableList";

import { useTableSearch } from "@/utils";
import { useSnackBar } from "@/utils/useSnackbar";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector, useAppDispatch } from "@/redux/hooks";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { useNotificationSocket } from "@/pages/Private/pages/Notifications/providers/NotificationSocketContext";
import { UserNotificationContentType } from "@/pages/Private/pages/Notifications/enum/notification.enum";
import { api } from "@/redux/api";
import { useAddPersonToSpecificListBulkMutation } from "@/pages/Private/redux/list/list.api";
import { useCreateBlacklistMutation } from "@/pages/Private/redux/blacklist/blacklist.api";
import {
	useFindMyConnectionsQuery,
	useSyncConnectionsMutation,
	useEnrichConnectionsMutation,
	useGetConnectionsFilterOptionsQuery,
	useGetLinkedinEnrichmentSettingsQuery,
} from "@/pages/Private/redux/account/account.api";

import { handleCopyClick } from "@/pages/Private/pages/List/utils/handleCopyClick";

import { UploadConnectionsModal } from "./modals/UploadConnectionsModal";
import {
	ConnectionsFilters,
	countActiveConnectionsFilters,
	EMPTY_CONNECTIONS_FILTERS,
	toConnectionsFilterParams,
} from "../schema/connections-filters";
import { LinkedinConnectionsFilterPanel } from "./LinkedinConnectionsFilterPanel";
import { LinkedinConnectionsToolbar } from "./LinkedinConnectionsToolbar";
import { connectionBulkAddItem } from "../utils/connectionBulkAddItem";
import { LinkedinConnectionsActionBar } from "./LinkedinConnectionsActionBar";
import { buildConnectionColumns } from "./LinkedinConnectionsColumns";
import { LinkedinConnectionsColumnModal } from "./LinkedinConnectionsColumnModal";

interface LinkedinConnectionsProps {
	amount: number;
}

export const LinkedinConnections: FunctionComponent<LinkedinConnectionsProps> = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.${key}`);

	const { user } = useAppSelector(authSelector);
	const dispatch = useAppDispatch();
	const { latestNotification } = useNotificationSocket();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [showUploadModal, setShowUploadModal] = useState(false);
	const [showSelectColumns, setShowSelectColumns] = useState(false);
	const [tableConfig, setTableConfig] = useLocalStorageState<TItem[]>(
		"linkedin-connections-columns",
		{ defaultValue: [] }
	);
	const [isSyncingInBackground, setIsSyncingInBackground] = useReducer(
		(_: boolean, next: boolean) => next,
		false
	);
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [selectAll, setSelectAll] = useState(false);
	const [filters, setFilters] = useState<ConnectionsFilters>(EMPTY_CONNECTIONS_FILTERS);
	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

	const { page, setPage, limit, setLimit, setSearchValue, query, searchValue } = useTableSearch();

	const [syncConnections, { isLoading: isSyncing }] = useSyncConnectionsMutation();
	const [enrichConnections, { isLoading: isEnrichLoading }] = useEnrichConnectionsMutation();
	const [addToList, { isLoading: isAddToListLoading }] = useAddPersonToSpecificListBulkMutation();
	const [createBlacklist, { isLoading: isBlacklistLoading }] = useCreateBlacklistMutation();

	const isConnectionSyncActive = useIsFeatureActive(FeaturesCodes.CONNECTION_SYNC);
	const isEnrichmentActive = useIsFeatureActive(FeaturesCodes.LI_CONNECTIONS_ENRICHMENT);
	const canSyncFromLinkedin = !!isConnectionSyncActive && !!user?.unipile?.isLoggedIn;

	const { data: enrichSettings } = useGetLinkedinEnrichmentSettingsQuery(undefined, {
		skip: !isEnrichmentActive,
	});
	const enrichEnabled = !!isEnrichmentActive && (enrichSettings?.enrich ?? false);

	const { data: filterOptions } = useGetConnectionsFilterOptionsQuery();

	const filterParams = useMemo(() => toConnectionsFilterParams(filters), [filters]);

	const { data, isLoading, isFetching } = useFindMyConnectionsQuery({
		page,
		limit,
		searchValue: query,
		...filterParams,
	});

	// The panel filters in the API, so a changed filter has to start again at page 1 — staying on
	// page 7 of a narrowed result set shows an empty table.
	const applyFilters = (next: ConnectionsFilters) => {
		setFilters(next);
		setPage(1);
		setSelectedIds([]);
		setSelectAll(false);
	};

	useEffect(() => {
		if (
			latestNotification?.contentType === UserNotificationContentType.CONNECTION_SYNC_DONE &&
			isSyncingInBackground
		) {
			setIsSyncingInBackground(false);
			dispatch(api.util.invalidateTags(["MyConnections", "Me"]));
		}
	}, [latestNotification, isSyncingInBackground, dispatch]);

	const handleSyncConnections = async () => {
		try {
			await syncConnections().unwrap();
			setIsSyncingInBackground(true);
			handleOpenSnackbar(ts("connectionSyncStarted"));
		} catch (err) {
			const msg =
				(err as FetchBaseQueryError)?.data &&
				typeof (err as FetchBaseQueryError).data === "object" &&
				"message" in ((err as FetchBaseQueryError).data as Record<string, unknown>)
					? String(((err as FetchBaseQueryError).data as Record<string, string>).message)
					: ts("connectionSyncError");

			handleOpenSnackbar(msg);
		}
	};

	const handleSelectRow = (id: number) => {
		setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	};

	const handleToggleSelectAll = () => {
		if (selectAll || selectedIds.length > 0) {
			setSelectAll(false);
			setSelectedIds([]);
		} else {
			setSelectAll(true);
			setSelectedIds((data?.data ?? []).map((r) => r.id));
		}
	};

	const handleEnrichSelected = async () => {
		if (selectedIds.length === 0) {
			return;
		}

		try {
			await enrichConnections({ userProspectIds: selectedIds }).unwrap();
			handleOpenSnackbar(
				ts("myConnections.enrichSuccess").replace("{{count}}", String(selectedIds.length))
			);
			setSelectedIds([]);
		} catch {
			handleOpenSnackbar(ts("myConnections.enrichError"));
		}
	};

	const handleEnrichRow = async (id: number) => {
		try {
			await enrichConnections({ userProspectIds: [id] }).unwrap();
			handleOpenSnackbar(ts("myConnections.enrichSuccess").replace("{{count}}", "1"));
		} catch {
			handleOpenSnackbar(ts("myConnections.enrichError"));
		}
	};

	const handleAddToList = async (list: { id: number; name: string }) => {
		const rows = (data?.data ?? []).filter((r) => selectedIds.includes(r.id));
		const persons = rows.map(connectionBulkAddItem);

		try {
			await addToList({ listId: list.id, persons }).unwrap();
			handleOpenSnackbar(ts("myConnections.addToListSuccess"));
			setSelectedIds([]);
		} catch {
			handleOpenSnackbar(ts("myConnections.addToListError"));
		}
	};

	const handleCreateAndAddToList = async (name: string) => {
		const rows = (data?.data ?? []).filter((r) => selectedIds.includes(r.id));
		const persons = rows.map(connectionBulkAddItem);

		try {
			await addToList({ listName: name, persons }).unwrap();
			handleOpenSnackbar(ts("myConnections.addToListSuccess"));
			setSelectedIds([]);
		} catch {
			handleOpenSnackbar(ts("myConnections.addToListError"));
		}
	};

	const handleBlacklist = async () => {
		const rows = (data?.data ?? []).filter((r) => selectedIds.includes(r.id));

		try {
			await Promise.all(
				rows.map((r) =>
					createBlacklist({
						linkedinUrl: r.linkedinUrl || undefined,
						email: r.email || undefined,
					}).unwrap()
				)
			);
			handleOpenSnackbar(t("account.myConnections.blacklistSuccess", { count: rows.length }));
			setSelectedIds([]);
		} catch {
			handleOpenSnackbar(ts("myConnections.blacklistError"));
		}
	};

	const rawColumns = useMemo(
		() =>
			buildConnectionColumns({
				selectedIds,
				onSelectRow: handleSelectRow,
				selectAll,
				onToggleSelectAll: handleToggleSelectAll,
				enrichEnabled,
				onEnrichRow: handleEnrichRow,
				handleCopyClick,
				ts,
			}),
		[selectedIds, selectAll, enrichEnabled]
	);

	const columns = useMemo(() => {
		if (!tableConfig?.length) {
			return rawColumns;
		}

		return rawColumns.map((c) => {
			const config = tableConfig.find((tc) => tc?.uid === c?.uid);

			if (config) {
				c.show = config.show;
				c.locked = config.locked as TableColumn["locked"];
			}

			return c;
		});
	}, [rawColumns, tableConfig]);

	const resetTableConfig = (force = false) => {
		const newConfig: TItem[] = rawColumns.map((c: TableColumn, index: number) => ({
			uid: c.uid ?? "",
			id: index + 1,
			show: typeof c.show !== "undefined" ? c.show : true,
			content: c.title,
			locked: !!c.locked || false,
			allowExclude: typeof c.allowExclude !== "undefined" ? c.allowExclude : true,
			custom: false,
			moveable: c.moveable,
			hideable: c.hideable,
		}));

		if (tableConfig.length === 0 || force) {
			setTableConfig(newConfig);
		}
	};

	useMemo(() => {
		if (rawColumns.length > 0 && tableConfig.length === 0) {
			resetTableConfig();
		}
	}, [rawColumns]);

	return (
		<div className="-mx-4 -mt-4 flex flex-col pb-[38px]">
			<LinkedinConnectionsToolbar
				activeFilterCount={countActiveConnectionsFilters(filters)}
				canSyncFromLinkedin={canSyncFromLinkedin}
				isFilterPanelOpen={isFilterPanelOpen}
				isSyncing={isSyncing || isSyncingInBackground}
				searchValue={searchValue}
				onSearchChange={setSearchValue}
				onSelectColumns={() => setShowSelectColumns(true)}
				onSync={handleSyncConnections}
				onToggleFilterPanel={() => setIsFilterPanelOpen((open) => !open)}
				onUpload={() => setShowUploadModal(true)}
			/>

			{isFilterPanelOpen && (
				<LinkedinConnectionsFilterPanel
					filters={filters}
					options={filterOptions}
					showEnrichmentFilter={!!isEnrichmentActive}
					onChange={applyFilters}
					onReset={() => applyFilters(EMPTY_CONNECTIONS_FILTERS)}
				/>
			)}

			<div className="px-2 pt-3">
				<UploadConnectionsModal
					handleClose={() => setShowUploadModal(false)}
					isVisible={showUploadModal}
				/>
				{showSelectColumns && (
					<LinkedinConnectionsColumnModal
						handleResetToDefault={() => {
							setTableConfig([]);
							setTimeout(() => resetTableConfig(true), 100);
						}}
						setShowSelectColumns={setShowSelectColumns}
						setTableConfig={setTableConfig}
						showSelectColumns={showSelectColumns}
						tableConfig={tableConfig}
					/>
				)}
				<Table
					columns={columns}
					data={data?.data ?? []}
					handleChangeRowsPerPage={(l: number) => setLimit(l)}
					handleFetchPage={(p: number) => setPage(p)}
					hidePagination
					isFetching={isFetching}
					isLoading={isLoading}
					limit={limit}
					overallCount={data?.meta.itemCount}
					page={page}
					sticky={false}
					totalPages={data?.meta.pageCount}
				/>
				{renderMessageSnackbar()}
			</div>

			<LinkedinConnectionsActionBar
				clearSelection={() => setSelectedIds([])}
				currentPage={page}
				enrichEnabled={enrichEnabled}
				handleChangeRowsPerPage={(l) => setLimit(l)}
				handleNavigateToPage={(p) => setPage(p)}
				isAddToListLoading={isAddToListLoading}
				isBlacklistLoading={isBlacklistLoading}
				isEnrichLoading={isEnrichLoading}
				limit={limit}
				overallCount={data?.meta.itemCount}
				selectedCount={selectedIds.length}
				totalPages={data?.meta.pageCount}
				onAddToList={handleAddToList}
				onBlacklist={handleBlacklist}
				onCreateAndAddToList={handleCreateAndAddToList}
				onEnrich={handleEnrichSelected}
				onExport={() => {}}
			/>
		</div>
	);
};
